import { AlertSettingsItem, DEFAULT_LIMIT, UserUnitSettings } from '../common/constants'
import { apiRequestProtected, apiRequestPublic } from '../utils/axios'

async function healthcheck(): Promise<any> {
  return apiRequestPublic('GET', '/healthcheck')
}

async function healthcheckProtected(): Promise<any> {
  return apiRequestProtected('GET', '/healthcheck-protected')
}

async function signIn(email: string, password: string): Promise<any> {
  return apiRequestPublic('POST', '/login', {
    email,
    password,
  })
}

async function getDevices(formatted = false, withConfigs = false): Promise<any> {
  return await apiRequestProtected('GET', `/devices?formatted=${formatted}&withConfigs=${withConfigs}`)
}

async function getDeviceConfig(sigfoxId: string, formatted = true): Promise<any> {
  return await apiRequestProtected('GET', `/device-configs?sigfoxId=${sigfoxId}&formatted=${formatted}`)
}

async function getSensorConfig(
  sigfoxId: string,
  timestamp: number = Math.floor(Date.now() / 1000),
  showAllSensors: boolean = false
): Promise<any> {
  return await apiRequestProtected(
    'GET',
    `/sensor-configs?sigfoxId=${sigfoxId}&timestamp=${timestamp}&showAllSensors=${showAllSensors}`
  )
}

async function getRawConfigMessages(
  sigfoxId: string,
  deviceConfig: boolean = true,
  sensorConfig: boolean = true
): Promise<any> {
  return await apiRequestProtected(
    'GET',
    `/raw-messages-configs?sigfoxId=${sigfoxId}&deviceConfig=${deviceConfig}&sensorConfig=${sensorConfig}`
  )
}

async function getUserUnits(): Promise<any> {
  return apiRequestProtected('GET', '/user-unit-settings')
}

async function getUnitConversions(): Promise<any> {
  return apiRequestProtected('GET', '/unit-conversions')
}

async function updateUserUnitSettings(userUnitSettings: UserUnitSettings): Promise<any> {
  return apiRequestProtected('POST', '/user-unit-settings', { userUnitSettings })
}

async function getReadings(sigfoxId: string, from: number, to: number, limit: number = DEFAULT_LIMIT): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  return apiRequestProtected('GET', `/readings-fast?sigfoxId=${sigfoxId}&from=${from}&to=${to}&limit=${limit}`)
}

async function hideReadings(sigfoxId: string, from: number, to: number): Promise<any> {
  return apiRequestProtected('POST', `/hide-readings`, { sigfoxId, from, to })
}

async function unhideReadings(sigfoxId: string): Promise<any> {
  return apiRequestProtected('POST', `/unhide-readings`, { sigfoxId })
}

async function getBatteryReadings(sigfoxId: string, from: number, to: number, limit: number = 5000): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  return apiRequestProtected('GET', `/battery-readings?sigfoxId=${sigfoxId}&from=${from}&to=${to}&limit=${limit}`)
}

async function getLastReadings(sigfoxId: string): Promise<any> {
  // TODO: add conversion mechanisms to this for flowmeter
  const timestamp = Math.floor(Date.now() / 1000)
  const response = apiRequestProtected('GET', `/readings-fast?sigfoxId=${sigfoxId}&to=${timestamp}&limit=1`)
  return response
}

async function getSensorConfigs(sigfoxId: string, timestamp: number, showAllSensors: boolean = false): Promise<any> {
  return apiRequestProtected(
    'GET',
    `/sensor-configs?sigfoxId=${sigfoxId}&timestamp=${timestamp}&showAllSensors=${showAllSensors}`
  )
}

async function getRawSigfoxMessages(
  sigfoxId: string,
  to: number,
  from: number,
  order: string = 'asc',
  limit: number = 2500
): Promise<any> {
  return apiRequestProtected(
    'GET',
    `/raw-messages?sigfoxId=${sigfoxId}&from=${from}&to=${to}&limit=${limit}&order=${order}`
  )
}

async function getHysteresisSettings(): Promise<any> {
  return apiRequestProtected('GET', '/hysteresis')
}

async function updateHysteresisSettings(hysteresis: number): Promise<any> {
  return apiRequestProtected('POST', '/hysteresis', {
    hysteresis,
  })
}

async function getAlertControls(): Promise<any> {
  return apiRequestProtected('GET', '/alert-controls')
}

async function updateAlertControls(settings: object): Promise<any> {
  return apiRequestProtected('POST', '/alert-controls', { ...settings })
}

async function getAlertSettings(deviceId: number): Promise<any> {
  return apiRequestProtected('GET', '/alert-settings' + (deviceId ? `?deviceId=${deviceId}` : ''))
}

async function createAlertSettings(settings: AlertSettingsItem): Promise<any> {
  return apiRequestProtected('POST', '/alert-settings', { ...settings })
}

async function updateAlertSettings(settings: AlertSettingsItem): Promise<any> {
  return apiRequestProtected('PATCH', '/alert-settings', { ...settings })
}

async function getSensorPosition(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-position?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function getSensorThresholds(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-thresholds?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function setSensorThresholds(deviceId: number, sensorId: number, data: object): Promise<any> {
  return apiRequestProtected('POST', `/sensor-thresholds`, {
    deviceId,
    sensorId,
    ...data,
  })
}

async function getSensorAlerts(deviceId: number, sensorId: number): Promise<any> {
  return apiRequestProtected('GET', `/sensor-alerts?deviceId=${deviceId}&sensorId=${sensorId}`)
}

async function setSensorAlerts(deviceId: number, sensorId: number, data: object): Promise<any> {
  return apiRequestProtected('POST', `/sensor-alerts`, {
    deviceId,
    sensorId,
    ...data,
  })
}

export const api = {
  healthcheck,
  healthcheckProtected,
  signIn,
  getDevices,
  getDeviceConfig,
  getSensorConfig,
  getUserUnits,
  getUnitConversions,
  updateUserUnitSettings,
  getReadings,
  hideReadings,
  unhideReadings,
  getLastReadings,
  getBatteryReadings,
  getSensorConfigs,
  getRawConfigMessages,
  getRawSigfoxMessages,
  getHysteresisSettings,
  updateHysteresisSettings,
  getAlertControls,
  updateAlertControls,
  getAlertSettings,
  createAlertSettings,
  updateAlertSettings,
  getSensorPosition,
  getSensorThresholds,
  setSensorThresholds,
  getSensorAlerts,
  setSensorAlerts,
}
